import { db, storage } from '../firebase.js';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { activeCollectionPeriod, inferCollectionPeriod } from './collection-period.js';
import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, addDoc,
  query, where, orderBy, limit, serverTimestamp, arrayUnion, runTransaction
} from 'firebase/firestore';

const studentsRef = collection(db, 'students');
const enrolmentsRef = collection(db, 'enrolments');

// Who/when provenance stamp. Returns the current user's display name + email so
// every student-area save records WHO last amended it alongside the serverside
// updatedAt. Dynamic import avoids the auth↔students cycle; never throws (an
// anonymous/edge save still persists, just without the name).
async function whoStamp() {
  try {
    const { getCurrentUser, getCurrentDisplayName } = await import('../auth.js');
    const u = getCurrentUser();
    const name = (typeof getCurrentDisplayName === 'function' && getCurrentDisplayName())
      || u?.displayName || (u?.email || '').split('@')[0] || '';
    return { updatedBy: name, updatedByEmail: u?.email || '' };
  } catch { return { updatedBy: '', updatedByEmail: '' }; }
}

// === Students ===
// Scoped classroom users (non-empty users/{uid}.scopeStudentIds) are not
// permitted to `list` the whole students collection server-side — they fetch
// their scoped students by id (each read gated by canViewStudentId). Everyone
// who can see the whole school (leadership/admin/assistant/unscoped) does the
// single bulk getDocs as before. canSeeAllStudents() is kept in lock-step with
// the `students` list rule in firestore.rules. Dynamic import avoids a static
// auth↔students import cycle.
export async function loadStudents() {
  const { canSeeAllStudents, getScopeStudentIds } = await import('../auth.js');
  if (!canSeeAllStudents()) {
    const ids = getScopeStudentIds();
    const snaps = await Promise.all(ids.map(id => getDoc(doc(db, 'students', id))));
    return snaps.filter(s => s.exists()).map(d => ({ id: d.id, ...d.data() }));
  }
  const snap = await getDocs(studentsRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getStudent(id) {
  const snap = await getDoc(doc(db, 'students', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveStudent(data) {
  const id = data.id || doc(studentsRef).id;
  const now = serverTimestamp();
  const who = await whoStamp();
  const record = {
    ...data,
    id,
    updatedAt: now,
    updatedBy: who.updatedBy,
    updatedByEmail: who.updatedByEmail,
    ...(data.createdAt ? {} : { createdAt: now, createdBy: who.updatedBy }),
  };
  await setDoc(doc(db, 'students', id), record, { merge: true });
  return id;
}

export async function deleteStudent(id) {
  await deleteDoc(doc(db, 'students', id));
}

// === Enrolments ===
export async function loadEnrolments() {
  const snap = await getDocs(enrolmentsRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Returns { id, created }. `created` is true only when a NEW enrolment doc was
// added (vs. merge-updating a pre-existing one). Import undo relies on this so it
// deletes only enrolments the import created — never a pre-existing enrolment it
// merely updated (which would make the student vanish from that year on undo).
export async function setEnrolment(studentId, academicYear, yearLevel, extra = {}) {
  const q = query(enrolmentsRef, where('studentId', '==', studentId), where('academicYear', '==', academicYear));
  const snap = await getDocs(q);
  if (snap.docs.length > 0) {
    await setDoc(snap.docs[0].ref, { yearLevel, ...extra }, { merge: true });
    return { id: snap.docs[0].id, created: false };
  }
  const ref = await addDoc(enrolmentsRef, { studentId, academicYear, yearLevel, ...extra });
  return { id: ref.id, created: true };
}

export async function removeEnrolment(studentId, academicYear) {
  const q = query(enrolmentsRef, where('studentId', '==', studentId), where('academicYear', '==', academicYear));
  const snap = await getDocs(q);
  for (const d of snap.docs) await deleteDoc(d.ref);
}

// === Settings ===
export async function loadSettings() {
  const snap = await getDoc(doc(db, 'settings', 'school'));
  return snap.exists() ? snap.data() : { years: [], logo: '' };
}

export async function saveSetting(key, value) {
  await setDoc(doc(db, 'settings', 'school'), { [key]: value }, { merge: true });
}

// === Backup / restore ===
//
// Exports the entire data set as a JSON file the user can download. Restore
// reads the same JSON and replays it. Both are leadership-only (enforced via
// the UI; the underlying writes still go through Firestore rules).

export async function exportFullBackup() {
  const out = { exportedAt: new Date().toISOString(), version: 1 };
  // Settings
  const settingsKeys = ['school', 'allowlist', 'yearArchives'];
  out.settings = {};
  for (const k of settingsKeys) {
    const s = await getDoc(doc(db, 'settings', k));
    if (s.exists()) out.settings[k] = s.data();
  }
  // Config
  out.config = {};
  for (const k of ['funnel', 'disorders', 'trackers', 'meetingTypes']) {
    const s = await getDoc(doc(db, 'config', k));
    if (s.exists()) out.config[k] = s.data();
  }
  // Students + their full subcollections
  const students = await loadStudents();
  out.students = [];
  for (const stu of students) {
    const profile = await loadProfile(stu.id);
    const notes = await loadNotes(stu.id);
    const meetings = await loadMeetings(stu.id);
    const emails = await loadEmails(stu.id);
    out.students.push({ ...stu, _profile: profile, _notes: notes, _meetings: meetings, _emails: emails });
  }
  out.enrolments = await loadEnrolments();
  return out;
}

/**
 * Restore from a JSON backup created by exportFullBackup.
 * WIPES all existing students/enrolments/timetables then rebuilds from the file.
 */
export async function restoreFromBackup(data) {
  // 1. Wipe existing students + subcollections + enrolments + timetables
  const studentsSnap = await getDocs(collection(db, 'students'));
  for (const sDoc of studentsSnap.docs) {
    for (const sub of ['notes', 'meetings', 'profile', 'accessLog', 'emails']) {
      const subSnap = await getDocs(collection(db, 'students', sDoc.id, sub));
      for (const subDoc of subSnap.docs) await deleteDoc(subDoc.ref);
    }
    await deleteDoc(sDoc.ref);
  }
  const enrSnap = await getDocs(enrolmentsRef);
  for (const d of enrSnap.docs) await deleteDoc(d.ref);
  const ttSnap = await getDocs(collection(db, 'esoTimetables'));
  for (const d of ttSnap.docs) await deleteDoc(d.ref);

  // 2. Restore settings
  if (data.settings) {
    for (const [k, v] of Object.entries(data.settings)) {
      await setDoc(doc(db, 'settings', k), v, { merge: true });
    }
  }
  // 3. Restore config
  if (data.config) {
    for (const [k, v] of Object.entries(data.config)) {
      await setDoc(doc(db, 'config', k), v, { merge: true });
    }
  }
  // 4. Restore students + subcollections
  for (const stu of (data.students || [])) {
    const { _profile, _notes, _meetings, _emails, ...stuData } = stu;
    const stuId = stuData.id || doc(collection(db, 'students')).id;
    stuData.id = stuId;
    await setDoc(doc(db, 'students', stuId), stuData);
    // Profile docs
    if (_profile) {
      for (const [section, sData] of Object.entries(_profile)) {
        if (sData && typeof sData === 'object') {
          await setDoc(doc(db, 'students', stuId, 'profile', section), sData);
        }
      }
    }
    // Notes
    for (const note of (_notes || [])) {
      const { id: _nid, ...noteData } = note;
      await addDoc(collection(db, 'students', stuId, 'notes'), noteData);
    }
    // Meetings
    for (const mtg of (_meetings || [])) {
      const { id: _mid, ...mtgData } = mtg;
      await addDoc(collection(db, 'students', stuId, 'meetings'), mtgData);
    }
    // Emails
    for (const em of (_emails || [])) {
      const { id: _eid, ...emData } = em;
      await addDoc(collection(db, 'students', stuId, 'emails'), emData);
    }
  }
  // 5. Restore enrolments
  for (const enr of (data.enrolments || [])) {
    const { id: _eid, ...enrData } = enr;
    await addDoc(enrolmentsRef, enrData);
  }
}

// === Year archive + rollover ===
//
// Archived years are read-only for non-leadership users. Leadership can
// unlock + edit. This is enforced in the UI; security rules also check
// `/settings/yearArchives.{year}.locked` before allowing writes when the
// active year matches an archived year.

export async function loadYearArchives() {
  try {
    const snap = await getDoc(doc(db, 'settings', 'yearArchives'));
    return snap.exists() ? (snap.data() || {}) : {};
  } catch { return {}; }
}

export async function setYearArchive(year, archived, archivedBy) {
  const ref = doc(db, 'settings', 'yearArchives');
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  if (archived) {
    data[year] = { locked: true, archivedAt: new Date().toISOString(), archivedBy: archivedBy || 'unknown' };
  } else {
    data[year] = { locked: false, unlockedAt: new Date().toISOString(), unlockedBy: archivedBy || 'unknown', wasArchived: true };
  }
  await setDoc(ref, data);
  return data;
}

/**
 * Roll over students from `fromYear` to `toYear`. Each student's enrolment is
 * cloned with the year level bumped by one ("Prep" → "Year 1", "Year 1" →
 * "Year 2", etc). The previous year's data stays intact and is automatically
 * archived (locked) once rollover completes. The student's profile, goals,
 * and currently-active plans are copied into the new year (notes are NOT
 * carried over since they are year-tagged).
 */
// 'Pre-Prep' sits before Prep so sign-ups added ahead of time roll straight
// into Prep on the year rollover (bumpYearLevel('Pre-Prep') === 'Prep').
const YEAR_LEVEL_ORDER = ['Pre-Prep', 'Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];
function bumpYearLevel(yl) {
  const idx = YEAR_LEVEL_ORDER.indexOf(yl);
  if (idx === -1) return yl;
  // At the top of the primary range there is no "next" — caller must
  // graduate (archive) the student rather than bump.
  if (idx === YEAR_LEVEL_ORDER.length - 1) return null;
  return YEAR_LEVEL_ORDER[idx + 1];
}

// Read the school's configured final year level (defaults to 'Year 6' for
// primary). Stored at /settings/school.finalYearLevel — leadership can edit.
async function getFinalYearLevel() {
  try {
    const snap = await getDoc(doc(db, 'settings', 'school'));
    const data = snap.exists() ? snap.data() : {};
    return data.finalYearLevel || 'Year 6';
  } catch { return 'Year 6'; }
}

/**
 * Freeze a read-only snapshot of a student's whole record for one academic
 * year into students/{id}/yearSnapshots/{yearId}. Diagnostic docs are
 * deliberately NOT frozen in — they persist live across every year and the
 * history viewer reads them from the current record. Notes are filtered to
 * that year (they're year-tagged); profile (goals/selections/mtss/summary) and
 * meetings are captured as-is. Academic years contain "/" (e.g. "25/26") which
 * is illegal in a doc id, so the id swaps "/" → "-".
 */
export async function snapshotStudentYear(studentId, year, enrolment = null) {
  const yearId = String(year).replace(/\//g, '-');
  const [profile, notes, meetings] = await Promise.all([
    loadProfile(studentId).catch(() => ({})),
    loadNotes(studentId).catch(() => []),
    loadMeetings(studentId).catch(() => []),
  ]);
  const profileCopy = JSON.parse(JSON.stringify(profile || {}));
  if (profileCopy.summary) delete profileCopy.summary.diagnosticFiles;
  const snapshot = {
    studentId,
    year,
    enrolment: enrolment ? { yearLevel: enrolment.yearLevel || '', classId: enrolment.classId || '' } : null,
    profile: profileCopy,
    notes: (notes || []).filter(n => !n.year || String(n.year) === String(year)),
    meetings: meetings || [],
    snapshotAt: new Date().toISOString(),
  };
  await setDoc(doc(db, 'students', studentId, 'yearSnapshots', yearId), snapshot);
  return snapshot;
}

/** Load archived (graduated) students from /archive — top-level docs only
 *  (name, year level, diagnoses, archivedAt). Used by historical search.
 *  Leadership-readable per the security rules; returns [] on any failure. */
export async function loadArchivedStudents() {
  try {
    const snap = await getDocs(collection(db, 'archive'));
    return snap.docs.map(d => ({ id: d.id, _archived: true, ...d.data() }));
  } catch { return []; }
}

/** Load a frozen year snapshot for a student, or null if none was taken. */
export async function loadYearSnapshot(studentId, year) {
  const yearId = String(year).replace(/\//g, '-');
  const snap = await getDoc(doc(db, 'students', studentId, 'yearSnapshots', yearId));
  return snap.exists() ? snap.data() : null;
}

export async function rolloverYear(fromYear, toYear, { archivePrevious = true, archivedBy = 'unknown' } = {}) {
  if (!fromYear || !toYear) throw new Error('Both years required');
  const finalYl = await getFinalYearLevel();
  // 1. Find all enrolments for the source year
  const enrSnap = await getDocs(query(enrolmentsRef, where('academicYear', '==', fromYear)));
  let promoted = 0, graduated = 0;
  // Graduating/leaving cohort gets a consistent leave date = the rollover date,
  // so they sit alongside specifically-removed students in Past Students.
  const leaveDate = new Date().toISOString().slice(0, 10);
  // Mark a student as "left the school" (kept in /students for 75-year
  // retention + visible in Past Students), rather than removing them. This
  // unifies graduates with specifically-removed leavers.
  const markLeft = async (studentId) => {
    await setDoc(doc(studentsRef, studentId), { leftSchool: true, leftDate: leaveDate, leftReason: 'graduated' }, { merge: true });
  };
  for (const enrDoc of enrSnap.docs) {
    const enr = enrDoc.data();
    // Freeze this student's outgoing-year record so it can be viewed read-only
    // from the History tab after the year is archived. Best-effort — a failure
    // here must never block the rollover.
    try { await snapshotStudentYear(enr.studentId, fromYear, enr); } catch (e) { console.warn('Year snapshot failed for', enr.studentId, e); }
    // Final-year graduates: mark as "left" with the rollover date. They stay
    // in /students (retained + findable + shown in Past Students) instead of
    // being moved to the locked /archive store.
    if (enr.yearLevel === finalYl) {
      try { await markLeft(enr.studentId); graduated++; }
      catch (e) { console.warn(`Could not mark ${enr.studentId} as graduated:`, e?.message || e); }
      continue;
    }
    const newYl = bumpYearLevel(enr.yearLevel);
    // Can't bump (no next year level) — treat as a leaver too.
    if (!newYl) { try { await markLeft(enr.studentId); } catch { /* best-effort */ } graduated++; continue; }
    // Skip if a target enrolment already exists
    const existing = await getDocs(query(enrolmentsRef, where('studentId', '==', enr.studentId), where('academicYear', '==', toYear)));
    if (existing.docs.length === 0) {
      await addDoc(enrolmentsRef, { studentId: enr.studentId, academicYear: toYear, yearLevel: newYl });
      promoted++;
    }
    // Carry last year's last term's goals forward into the new year's
    // first term, then WIPE the per-goal data so the new year starts
    // clean. Academic-year term order in this app is Term 3 → 4 → 1
    // → 2, so Term 2 is the last term of the outgoing year and Term 3
    // is the first term of the incoming year.
    //
    // Stable per-student data (NCCD strip, long-term goal, strengths,
    // interests) is preserved. Section F (annual review) is cleared
    // because it describes the year that just ended.
    try {
      const profile = await loadProfile(enr.studentId);
      if (profile?.goals) {
        const goals = { ...profile.goals };
        const lastTerm = goals.term_Term_2 || {};
        const count = Math.max(1, Math.min(3, parseInt(lastTerm.goalCount, 10) || 1));
        const summaryLines = [];
        const reviewLines = [];
        const nextStepsLines = [];
        for (let i = 0; i < count; i++) {
          const g = lastTerm['goal_' + i];
          if (!g) continue;
          const tag = g.priorityDomain ? `[${g.priorityDomain}] ` : '';
          if (g.smartGoal) summaryLines.push(`• ${tag}${g.smartGoal.trim()}${g.progressPct != null ? ` (${g.progressPct}% · ${g.achievementLevel || '-'})` : ''}`);
          if (g.achieved) reviewLines.push(`• ${tag}${g.achieved.trim()}`);
          if (g.nextSteps) nextStepsLines.push(`• ${tag}${g.nextSteps.trim()}`);
        }
        // Build the fresh goals doc: keep stable fields, drop per-term data.
        const wiped = {
          longTermGoal: goals.longTermGoal || '',
          interests: goals.interests || '',
          strengths: goals.strengths || '',
          interests_lastUpdate: goals.interests_lastUpdate || '',
          strengths_lastUpdate: goals.strengths_lastUpdate || '',
          nccdCategory: goals.nccdCategory || '',
          nccdAdjustmentLevel: goals.nccdAdjustmentLevel || '',
          nccdDiagnosis: goals.nccdDiagnosis || '',
          // Fresh, empty terms — Term 3 carries last year's summary.
          term_Term_3: {
            goalsFromLastTerm: summaryLines.join('\n'),
            lastTermReview:    reviewLines.join('\n'),
            lastTermNextSteps: nextStepsLines.join('\n'),
            goalCount: '1',
            goal_0: {},
          },
          term_Term_4: { goalCount: '1', goal_0: {} },
          term_Term_1: { goalCount: '1', goal_0: {} },
          term_Term_2: { goalCount: '1', goal_0: {} },
          // Section F cleared — it described the year just ended.
        };
        await saveProfile(enr.studentId, 'goals', wiped);
      }
    } catch (e) {
      console.warn('Goal rollover/wipe failed for', enr.studentId, e);
    }
  }
  // 2. Add the new year to settings.school.years if not present
  const schoolSnap = await getDoc(doc(db, 'settings', 'school'));
  const school = schoolSnap.exists() ? schoolSnap.data() : { years: [] };
  if (!school.years) school.years = [];
  if (!school.years.includes(toYear)) {
    school.years.push(toYear);
    school.years.sort();
    await setDoc(doc(db, 'settings', 'school'), school, { merge: true });
  }
  // 3. Archive the previous year (locks it)
  if (archivePrevious) {
    await setYearArchive(fromYear, true, archivedBy);
  }
  return { promoted, graduated };
}

// === Import batches (undo support) ===
//
// Every CSV import via the wizard records a /importBatches/{batchId} doc
// describing what it created. The Undo flow loads that doc and removes / reverts
// the artefacts. Older entries (>90 days) can be pruned manually if desired.

// Throws on Firestore error so callers can distinguish "load failed" from
// "no batches yet" — the recent-imports panel relies on this so a transient
// permission/network blip doesn't silently wipe the sidebar.
export async function loadImportBatches({ limitTo = 20 } = {}) {
  const snap = await getDocs(query(collection(db, 'importBatches'), orderBy('createdAt', 'desc')));
  return snap.docs.slice(0, limitTo).map(d => ({ id: d.id, ...d.data() }));
}

export async function undoImportBatch(batchId) {
  const ref = doc(db, 'importBatches', batchId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Batch not found');
  const batch = snap.data();
  const summary = batch.summary || {};
  const counts = { studentsRemoved: 0, enrolmentsRemoved: 0, notesRemoved: 0, sessionsRemoved: 0, summaryRowsRemoved: 0, profilesRestored: 0 };

  switch (batch.type) {
    case 'students': {
      // Delete every student created by this batch + their subcollections + enrolments.
      for (const sid of summary.studentIds || []) {
        try {
          // Subcollections
          const subs = ['notes', 'meetings', 'emails', 'profile', 'accessLog'];
          for (const sub of subs) {
            const subSnap = await getDocs(collection(db, 'students', sid, sub));
            for (const d of subSnap.docs) await deleteDoc(d.ref);
          }
          await deleteDoc(doc(db, 'students', sid));
          counts.studentsRemoved++;
        } catch (e) { console.warn('Could not delete', sid, e); }
      }
      for (const eid of summary.enrolmentIds || []) {
        try { await deleteDoc(doc(db, 'enrolments', eid)); counts.enrolmentsRemoved++; }
        catch (e) { console.warn('Could not delete enrolment', eid, e); }
      }
      // Restore pre-update values for any rows that updated existing students.
      counts.profilesRestored = 0;
      for (const snap of (summary.updatedSnapshots || [])) {
        try {
          const { id, ...prev } = snap;
          if (!id) continue;
          await setDoc(doc(db, 'students', id), { ...prev, updatedAt: serverTimestamp() }, { merge: true });
          counts.profilesRestored++;
        } catch (e) { console.warn('Update restore failed', snap, e); }
      }
      break;
    }
    case 'diagnosesUpdate': {
      // Restore each student's previous nccdStatus / category / level / diagnoses.
      const prev = summary.previousValues || {};
      for (const [sid, before] of Object.entries(prev)) {
        try {
          await setDoc(doc(db, 'students', sid), {
            nccdStatus: before.nccdStatus || '',
            category: before.category || '',
            level: before.level || '',
            diagnoses: before.diagnoses || [],
            nccdExcluded: before.nccdExcluded || false,
            updatedAt: serverTimestamp(),
          }, { merge: true });
          counts.profilesRestored++;
        } catch (e) { console.warn('Restore failed for', sid, e); }
      }
      break;
    }
    case 'staffNotes': {
      for (const { studentId, noteId } of summary.noteRefs || []) {
        try { await deleteDoc(doc(db, 'students', studentId, 'notes', noteId)); counts.notesRemoved++; }
        catch (e) { console.warn('Note delete failed', e); }
      }
      break;
    }
    case 'timetable': {
      for (const sid of summary.sessionIds || []) {
        try { await deleteDoc(doc(db, 'sessions', sid)); counts.sessionsRemoved++; }
        catch (e) { console.warn('Session delete failed', e); }
      }
      break;
    }
    case 'attendance':
    case 'assessments': {
      // Walk profile/summary docs and strip any rows whose _importBatchId matches.
      for (const [sid, info] of Object.entries(summary.summaryAdditions || {})) {
        try {
          const profileRef = doc(db, 'students', sid, 'profile', 'summary');
          const psnap = await getDoc(profileRef);
          if (!psnap.exists()) continue;
          const data = psnap.data() || {};
          const section = data[info.sectionKey];
          if (!section || !Array.isArray(section.rows)) continue;
          const before = section.rows.length;
          section.rows = section.rows.filter(r => r?._importBatchId !== batchId);
          counts.summaryRowsRemoved += before - section.rows.length;
          await setDoc(profileRef, data);
        } catch (e) { console.warn('Summary row strip failed', e); }
      }
      break;
    }
    default:
      throw new Error('Unknown import batch type: ' + batch.type);
  }

  // Mark the batch as undone (don't delete — keep audit trail).
  await setDoc(ref, { undoneAt: serverTimestamp(), undoneCounts: counts }, { merge: true });
  return counts;
}

// === Note copy (to other students) ===
// Copies a note to one or more target students. The original stays in place.
// Each copy gets a `copiedFrom` field so it's traceable.
export async function copyNoteToStudents(fromStudentId, noteId, targetStudentIds) {
  const noteRef = doc(db, 'students', fromStudentId, 'notes', noteId);
  const snap = await getDoc(noteRef);
  if (!snap.exists()) throw new Error('Note not found');
  const data = snap.data();
  data.copiedFrom = fromStudentId;
  data.copiedAt = serverTimestamp();
  for (const targetId of targetStudentIds) {
    const targetRef = collection(db, 'students', targetId, 'notes');
    await addDoc(targetRef, data);
  }
}

// === ESO timetables (grade-level) ===
//
// One ESO timetable per (year level + academic year + term-window). Stored
// in /esoTimetables/{id} as:
//   { id, yearLevel, academicYear, termLabel, entries: [{ id, label, day,
//     start, end, notes, studentIds: [] }], updatedAt, updatedBy }
//
// `studentIds` is the list of students included in that ESO session — when
// rendering a student's own intervention timetable, any session whose
// studentIds contains that student is shown as a greyed background event.

const esoRef = collection(db, 'esoTimetables');

export async function loadEsoTimetables() {
  try {
    const snap = await getDocs(esoRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}

export async function loadEsoTimetable(id) {
  const snap = await getDoc(doc(db, 'esoTimetables', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveEsoTimetable(data) {
  const id = data.id || doc(esoRef).id;
  await setDoc(doc(db, 'esoTimetables', id), {
    ...data, id,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return id;
}

export async function deleteEsoTimetable(id) {
  await deleteDoc(doc(db, 'esoTimetables', id));
}

// Find every ESO session that includes this student, across all current
// ESO timetables. Used to overlay greyed background events on the student's
// own timetable view.
export async function loadEsoEntriesForStudent(studentId) {
  const all = await loadEsoTimetables();
  const out = [];
  all.forEach(t => {
    (t.entries || []).forEach(e => {
      if (Array.isArray(e.studentIds) && e.studentIds.includes(studentId)) {
        out.push({ ...e, _esoTimetableId: t.id, _esoLabel: t.termLabel || '', _esoYearLevel: t.yearLevel });
      }
    });
  });
  return out;
}

// === Sessions (unified timetable) ===
export async function loadSessions() {
  const snap = await getDocs(query(collection(db, 'sessions'), orderBy('day')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// === Intervention Groups ===
// Named, persistent groups of students set up on the Timetables → By
// Intervention Group view. Multiple sessions can be attached to the
// same group; new sessions auto-populate the group's student list so
// staff don't have to re-pick the kids each time.
export async function loadInterventionGroups() {
  const snap = await getDocs(collection(db, 'interventionGroups'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
export async function saveInterventionGroup(data) {
  const payload = { ...data, updatedAt: serverTimestamp() };
  if (data.id) {
    await updateDoc(doc(db, 'interventionGroups', data.id), payload);
    return data.id;
  }
  payload.createdAt = serverTimestamp();
  const ref = await addDoc(collection(db, 'interventionGroups'), payload);
  return ref.id;
}
export async function deleteInterventionGroup(id) {
  await deleteDoc(doc(db, 'interventionGroups', id));
}

/**
 * Archive an intervention group instead of deleting it.
 *
 * A school accumulates these fast - one per teacher per term per focus - and
 * the picker becomes unusable long before anyone wants to lose the history.
 * Archived groups drop out of the pickers but keep every session, note and
 * timetable entry recorded against them, so past terms and prior collection
 * periods still resolve the group they belonged to.
 */
export async function setInterventionGroupArchived(id, archived) {
  await updateDoc(doc(db, 'interventionGroups', id), {
    archived: !!archived,
    archivedAt: archived ? new Date().toISOString() : null,
    updatedAt: serverTimestamp(),
  });
}

// === Staff roster (teachers + ESOs) ===
//
// One `/staff/{id}` collection with a `kind` discriminator ('teacher' |
// 'eso'). Teachers get assigned to classes; ESOs get assigned directly to
// students (student.esoStaffIds[]). A login can be linked to a staff member
// (users.linkedStaffId) which drives what that login can see (see auth.js +
// settings.js scope expansion). Managed by Leadership / Admin only.
const staffRef = collection(db, 'staff');
const classesRef = collection(db, 'classes');

export async function loadStaff() {
  try {
    const snap = await getDocs(staffRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}
export async function saveStaff(data) {
  const id = data.id || doc(staffRef).id;
  const record = {
    kind: data.kind || 'teacher',
    name: (data.name || '').trim(),
    email: (data.email || '').trim(),
    active: data.active !== false,
    id,
    updatedAt: serverTimestamp(),
    ...(data.createdAt ? {} : { createdAt: serverTimestamp() }),
  };
  await setDoc(doc(db, 'staff', id), record, { merge: true });
  return id;
}
export async function deleteStaff(id) {
  await deleteDoc(doc(db, 'staff', id));
}

// === Classes ===
//
// `/classes/{id}` belongs to one academic year and carries a SET of year
// levels (composite-capable). teacherId → /staff. A student's membership
// lives on their enrolment (enrolment.classId).
export async function loadClasses() {
  try {
    const snap = await getDocs(classesRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}
export async function saveClass(data) {
  const id = data.id || doc(classesRef).id;
  const record = {
    name: (data.name || '').trim(),
    academicYear: data.academicYear,
    yearLevels: Array.isArray(data.yearLevels) ? data.yearLevels : [],
    // A class can have MULTIPLE teachers (all users). teacherId kept for
    // backwards-compatible reads; new writes populate teacherIds[].
    ...(Array.isArray(data.teacherIds) ? { teacherIds: data.teacherIds } : {}),
    teacherId: data.teacherId || null,
    id,
    updatedAt: serverTimestamp(),
    ...(data.createdAt ? {} : { createdAt: serverTimestamp(), createdBy: data.createdBy || '' }),
  };
  await setDoc(doc(db, 'classes', id), record, { merge: true });
  return id;
}
export async function deleteClass(id) {
  // Read the class first so we can refresh the teacher's linked login after.
  let teacherId = null;
  try { teacherId = (await getDoc(doc(db, 'classes', id))).data()?.teacherId || null; } catch {}
  // Detach every enrolment pointing at this class.
  const enrSnap = await getDocs(query(enrolmentsRef, where('classId', '==', id)));
  for (const d of enrSnap.docs) await setDoc(d.ref, { classId: null }, { merge: true });
  await deleteDoc(doc(db, 'classes', id));
  if (teacherId) await recomputeUserScopesForStaff([teacherId]);
}

/** Assign (or clear, with classId=null) a student's class for a year. */
export async function assignStudentToClass(studentId, academicYear, classId, yearLevel) {
  // Preserve the existing year level if not supplied.
  let yl = yearLevel;
  if (yl == null) {
    const snap = await getDocs(query(enrolmentsRef, where('studentId', '==', studentId), where('academicYear', '==', academicYear)));
    yl = snap.docs[0]?.data()?.yearLevel || '';
  }
  await setEnrolment(studentId, academicYear, yl, { classId: classId || null });
  // Refresh teachers of both the old and new class (best-effort via cache).
  const cls = classId ? (typeof window !== 'undefined' ? (window.nccd?.classes || []).find(c => c.id === classId) : null) : null;
  if (cls?.teacherId) await recomputeUserScopesForStaff([cls.teacherId]);
}

/** Set the ESO(s) assigned to a student. */
export async function assignEsosToStudent(studentId, esoStaffIds) {
  const ids = Array.isArray(esoStaffIds) ? esoStaffIds.filter(Boolean) : [];
  await saveStudent({ id: studentId, esoStaffIds: ids });
  if (ids.length) await recomputeUserScopesForStaff(ids);
}

// === Scope re-expansion ===
//
// Server-side rules can only enforce scope via the pre-expanded
// users.scopeStudentIds array (they can't join classes→enrolments→students).
// Whenever a roster / assignment changes we recompute scopeStudentIds for any
// login linked to the affected staff member(s). Best-effort: the dynamic
// canViewStudent() branch in auth.js covers the in-session UX gap.
export async function recomputeUserScopesForStaff(staffIds) {
  const wanted = (Array.isArray(staffIds) ? staffIds : [staffIds]).filter(Boolean);
  if (!wanted.length) return;
  try {
    const { studentIdsForTeacher, studentIdsForEso } = await import('./class-helpers.js');
    // Build a fresh context so the expansion is authoritative.
    const [students, enrolments, classes, staff] = await Promise.all([
      loadStudents(), loadEnrolments(), loadClasses(), loadStaff(),
    ]);
    const activeYear = (typeof window !== 'undefined' && window.nccd?.activeYear) || new Date().getFullYear().toString();
    const ctx = { students, enrolments, classes, staff, activeYear };
    const staffById = new Map(staff.map(s => [s.id, s]));

    const usersSnap = await getDocs(collection(db, 'users'));
    for (const u of usersSnap.docs) {
      const linked = u.data().linkedStaffId;
      if (!linked || !wanted.includes(linked)) continue;
      const sd = staffById.get(linked);
      const ids = sd?.kind === 'eso'
        ? studentIdsForEso(linked, ctx)
        : studentIdsForTeacher(linked, activeYear, ctx);
      await setDoc(u.ref, { scopeStudentIds: ids }, { merge: true });
    }
  } catch (e) {
    console.warn('recomputeUserScopesForStaff failed:', e?.message || e);
  }
}
// Back-compat aliases used by call sites that name the trigger by entity.
export const recomputeUserScopesForClass = (classId) => {
  const cls = (typeof window !== 'undefined' ? (window.nccd?.classes || []) : []).find(c => c.id === classId);
  return recomputeUserScopesForStaff(cls?.teacherId ? [cls.teacherId] : []);
};
export const recomputeUserScopesForEso = (esoStaffId) => recomputeUserScopesForStaff([esoStaffId]);

// === One-time migration: seed staff roster + classes from legacy fields ===
//
// Builds the teacher/ESO rosters from the distinct free-text teacherName /
// esoName values already on student docs, maps each student's esoName to
// esoStaffIds, and (optionally) auto-creates one class per (yearLevel,
// teacherName) for the active year, wiring up enrolment.classId. Composite
// classes are reshaped by hand afterwards. Idempotent: existing staff names
// and classes are reused, never duplicated. Leadership-only (UI-gated).
export async function migrateClassesAndStaff({ activeYear, createClasses = true } = {}) {
  const ay = activeYear || (typeof window !== 'undefined' && window.nccd?.activeYear) || new Date().getFullYear().toString();
  const [students, enrolments, existingStaff, existingClasses] = await Promise.all([
    loadStudents(), loadEnrolments(), loadStaff(), loadClasses(),
  ]);
  const norm = s => (s || '').trim().toLowerCase();
  const teacherByName = new Map(existingStaff.filter(s => s.kind === 'teacher').map(s => [norm(s.name), s.id]));
  const esoByName = new Map(existingStaff.filter(s => s.kind === 'eso').map(s => [norm(s.name), s.id]));
  let teachersCreated = 0, esosCreated = 0, classesCreated = 0, studentsUpdated = 0, enrolmentsLinked = 0;

  // 1. Seed teachers + ESOs.
  for (const s of students) {
    const tn = (s.teacherName || '').trim();
    if (tn && !teacherByName.has(norm(tn))) {
      const id = await saveStaff({ kind: 'teacher', name: tn });
      teacherByName.set(norm(tn), id); teachersCreated++;
    }
    const en = (s.esoName || '').trim();
    if (en && !esoByName.has(norm(en))) {
      const id = await saveStaff({ kind: 'eso', name: en });
      esoByName.set(norm(en), id); esosCreated++;
    }
  }

  // 2. Map each student's legacy esoName → esoStaffIds (if not already set).
  for (const s of students) {
    if (Array.isArray(s.esoStaffIds) && s.esoStaffIds.length) continue;
    const en = (s.esoName || '').trim();
    const id = en ? esoByName.get(norm(en)) : null;
    if (id) { await saveStudent({ id: s.id, esoStaffIds: [id] }); studentsUpdated++; }
  }

  // 3. Optionally auto-create a class per (yearLevel, teacherName) for the
  //    active year and link enrolments.
  if (createClasses) {
    const classByKey = new Map(); // key: `${yl}|${norm(teacher)}` -> classId
    existingClasses.filter(c => c.academicYear === ay).forEach(c => {
      const tName = existingStaff.find(s => s.id === c.teacherId)?.name || '';
      (c.yearLevels || []).forEach(yl => classByKey.set(`${yl}|${norm(tName)}`, c.id));
    });
    const studentById = new Map(students.map(s => [s.id, s]));
    for (const enr of enrolments) {
      if (enr.academicYear !== ay || enr.classId) continue;
      const st = studentById.get(enr.studentId);
      const tn = (st?.teacherName || '').trim();
      if (!tn || !enr.yearLevel) continue;
      const key = `${enr.yearLevel}|${norm(tn)}`;
      let classId = classByKey.get(key);
      if (!classId) {
        classId = await saveClass({
          name: `${enr.yearLevel} - ${tn}`,
          academicYear: ay,
          yearLevels: [enr.yearLevel],
          teacherId: teacherByName.get(norm(tn)) || null,
        });
        classByKey.set(key, classId); classesCreated++;
      }
      await setEnrolment(enr.studentId, ay, enr.yearLevel, { classId });
      enrolmentsLinked++;
    }
  }

  return { teachersCreated, esosCreated, classesCreated, studentsUpdated, enrolmentsLinked };
}

export async function saveSession(data) {
  if (data.id) {
    await updateDoc(doc(db, 'sessions', data.id), { ...data, updatedAt: serverTimestamp() });
    return data.id;
  }
  const ref = await addDoc(collection(db, 'sessions'), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  return ref.id;
}

export async function deleteSession(id) {
  await deleteDoc(doc(db, 'sessions', id));
}

// === Bulk profile/meetings loader for cohort readiness ===
/**
 * Loads profile + meetings + notes for every student in parallel (concurrency-limited).
 * Returns a map of studentId -> { profile, meetings, notes }.
 *
 * Used by the Records page to compute proper NCCD readiness for the spreadsheet.
 */
export async function loadCohortDetails(studentIds, concurrency = 16, opts = {}) {
  // Cohort-wide fan-out — the records page runs this over EVERY student, so
  // what it fetches per student is multiplied by ~274 on a real school.
  //
  //  - notes were loaded for every student and used by NOBODY (records reads
  //    profile + meetings; the meeting scheduler reads meetings). Opt-in now.
  //  - the full profile meant a collection LIST, which every scoped role
  //    (teachers, ESOs, specialists) has denied → a ~13-doc-per-student
  //    fallback. Only summary.readiness is consumed, so read that ONE doc
  //    unless a caller asks for the whole profile.
  // Together with the same change in the mtss loader this took the records
  // page from ~7,700 reads to a few hundred ("the filters never load",
  // Harry, 18 Sep 2026).
  const { withNotes = false, fullProfile = false } = opts;
  const result = {};
  let i = 0;
  const next = async () => {
    while (i < studentIds.length) {
      const idx = i++;
      const id = studentIds[idx];
      try {
        const [profile, meetings, notes] = await Promise.all([
          fullProfile ? loadProfile(id) : loadProfileSection(id, 'summary').then(summary => ({ summary })),
          loadMeetings(id),
          withNotes ? loadNotes(id) : Promise.resolve([]),
        ]);
        result[id] = { profile, meetings, notes };
      } catch (e) {
        result[id] = { profile: {}, meetings: [], notes: [] };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, studentIds.length) }, next));
  return result;
}

// === Signature library ===
export async function loadInterventions() {
  try {
    const snap = await getDocs(query(collection(db, 'interventions'), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}

export async function loadSignatures() {
  try {
    const snap = await getDocs(collection(db, 'signatures'));
    // Alphabetical — this feeds the signature dropdowns directly.
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  } catch { return []; }
}
export async function saveSignature(sig) {
  const id = sig.id || doc(collection(db, 'signatures')).id;
  await setDoc(doc(db, 'signatures', id), { ...sig, id }, { merge: true });
  return id;
}
export async function deleteSignature(id) {
  await deleteDoc(doc(db, 'signatures', id));
}

// Fetch every user that has a signatureImage set. Used by Leadership /
// Owner to attach any staff member's signature to a plan or meeting.
// Returns library-shaped objects so they can be merged straight into the
// signatures dropdown (id, name, image, role).
// Load all user (login-account) records. In the unified people model a user IS
// a teacher / ESO / leader — their role + access scope drive everything, so the
// app loads these globally (window.nccd.users) alongside students.
export async function loadUsers() {
  try {
    const snap = await getDocs(collection(db, 'users'));
    // Alphabetical by display name (email fallback): window.nccd.users feeds
    // every staff list in the app (notify recipients, roles guide, pickers),
    // so one sort here keeps them all in reading order.
    return snap.docs.map(d => ({ uid: d.id, id: d.id, ...d.data() }))
      .sort((a, b) => String(a.displayName || a.email || '').localeCompare(String(b.displayName || b.email || '')));
  } catch { return []; }
}

export async function loadAllUserSignatures() {
  try {
    const snap = await getDocs(collection(db, 'users'));
    const out = [];
    snap.docs.forEach(d => {
      const u = d.data();
      if (!u?.signatureImage) return;
      out.push({
        id: 'user_' + d.id,
        name: u.signatureName || u.displayName || u.email || 'User',
        image: u.signatureImage,
        role: u.role ? String(u.role).replace(/_/g, ' ') : '',
        source: 'user',
        userEmail: u.email || '',
      });
    });
    // Alphabetical — feeds the "Staff signatures" optgroups directly.
    return out.sort((a, b) => a.name.localeCompare(b.name));
  } catch (e) {
    console.warn('loadAllUserSignatures failed:', e?.message || e);
    return [];
  }
}

// Per-user signature stored on /users/{uid}. Each logged-in user can save
// their own signature once and reuse it across all meetings/plans.
export async function loadMySignature(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (!d.signatureImage) return null;
    return { image: d.signatureImage, name: d.signatureName || d.displayName || '' };
  } catch { return null; }
}
export async function saveMySignature(uid, image, name) {
  if (!uid) throw new Error('saveMySignature: missing uid');
  await setDoc(doc(db, 'users', uid), {
    signatureImage: image || '',
    signatureName: name || '',
    signatureUpdatedAt: serverTimestamp(),
  }, { merge: true });
}
export async function deleteMySignature(uid) {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid), { signatureImage: '', signatureName: '' }, { merge: true });
}

// Display name override — every user's name is seeded from Google
// sign-in (`auth.currentUser.displayName`) into `/users/{uid}.displayName`
// and can then be edited from the My Profile page. Used to prefill the
// staff member name on the Notes form etc.
export async function loadMyDisplayName(uid) {
  if (!uid) return '';
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return '';
    return snap.data().displayName || '';
  } catch { return ''; }
}
export async function saveMyDisplayName(uid, name) {
  if (!uid) throw new Error('saveMyDisplayName: missing uid');
  await setDoc(doc(db, 'users', uid), {
    displayName: name || '',
    displayNameUpdatedAt: serverTimestamp(),
  }, { merge: true });
}

// === Notes ===
export async function loadNotes(studentId) {
  const notesRef = collection(db, 'students', studentId, 'notes');
  const snap = await getDocs(query(notesRef, orderBy('date', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** An id for a note before it is saved, so a retried save can never make a second copy. */
export function newNoteId(studentId) { return doc(collection(db, 'students', studentId, 'notes')).id; }

/**
 * Add a note. With `id` (from newNoteId) the save is safe to retry: if that
 * note is already there - a save that timed out on a slow connection but went
 * through - nothing more is written (Sam, 30 Oct 2026: one note saved several
 * times because "it didn't show her that it was saving").
 */
export async function addNote(studentId, noteData, { id = '' } = {}) {
  const notesRef = collection(db, 'students', studentId, 'notes');
  const who = await whoStamp();
  // Stamp the NCCD collection period alongside the academic year already on the
  // note (inferred from the note's date, defaulting to the active period).
  // Stamp with the ACTIVE (manually-managed) period so a note added right after
  // an early rollover lands in the period the school is now working in — and
  // stays visible under that period's filter. Falls back to date-inference.
  const collectionPeriod = noteData.collectionPeriod || activeCollectionPeriod() || inferCollectionPeriod(noteData.date);
  const payload = { ...noteData, collectionPeriod, createdAt: serverTimestamp(), createdBy: who.updatedBy, createdByEmail: who.updatedByEmail };
  if (id) {
    const ref = doc(notesRef, id);
    try { if ((await getDoc(ref)).exists()) return id; } catch { /* cannot tell: write it (same id, so still one note) */ }
    await setDoc(ref, payload);
    return id;
  }
  const ref = await addDoc(notesRef, payload);
  return ref.id;
}

export async function updateNote(studentId, noteId, patch) {
  const who = await whoStamp();
  await setDoc(doc(db, 'students', studentId, 'notes', noteId), { ...patch, updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail }, { merge: true });
}

export async function deleteNote(studentId, noteId) {
  await deleteDoc(doc(db, 'students', studentId, 'notes', noteId));
}

// === Intervention session log (dated per-occurrence summaries + Individualise) ===
// A recurring `sessions` doc gets a dated history: each summary is one occurrence
// (what happened that day). "Individualise" additionally splits the summary into
// per-student records saved under students/{id}/interventionLog.
export async function loadSessionSummaries(sessionId) {
  const ref = collection(db, 'sessions', sessionId, 'summaries');
  const snap = await getDocs(query(ref, orderBy('date', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveSessionSummary(sessionId, data) {
  const ref = collection(db, 'sessions', sessionId, 'summaries');
  const who = await whoStamp();
  const created = await addDoc(ref, { ...data, createdAt: serverTimestamp(), createdBy: who.updatedBy, createdByEmail: who.updatedByEmail });
  // Writing a note proves this session takes notes — flip its 📝 marker on
  // (sessions default OFF and are hidden from note pickers/student tabs).
  try { await updateDoc(doc(db, 'sessions', sessionId), { notesOn: true }); } catch { /* best-effort */ }
  return created.id;
}

export async function deleteSessionSummary(sessionId, summaryId) {
  await deleteDoc(doc(db, 'sessions', sessionId, 'summaries', summaryId));
}

// Per-student intervention record (one dated entry, tied back to its session).
export async function addInterventionLogEntry(studentId, entry) {
  const ref = collection(db, 'students', studentId, 'interventionLog');
  const who = await whoStamp();
  const collectionPeriod = entry.collectionPeriod || activeCollectionPeriod() || inferCollectionPeriod(entry.date);
  const d = await addDoc(ref, { ...entry, collectionPeriod, createdAt: serverTimestamp(), createdBy: who.updatedBy, createdByEmail: who.updatedByEmail });
  // Writing a per-student note proves this session takes notes — flip its 📝
  // marker on so the session appears in the note pickers / student Interventions
  // tab. Without this, a notes-only-per-student entry on a session that was
  // created with notesOn:false (e.g. a plain timetabled slot) saved silently
  // with nowhere to view it. Best-effort; needs a real sessionId.
  if (entry.sessionId) {
    try { await updateDoc(doc(db, 'sessions', entry.sessionId), { notesOn: true }); } catch { /* best-effort */ }
  }
  return d.id;
}

export async function loadInterventionLog(studentId) {
  const ref = collection(db, 'students', studentId, 'interventionLog');
  const snap = await getDocs(query(ref, orderBy('date', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// === Meetings ===
// Every meeting type the app writes — needed for the per-type fallback below.
// Config-defined types are merged in at call time so school-added types load.
export const MEETING_TYPE_IDS = [
  'learning_diversity', 'general', 'initial_assessment', 'new_enrolment',
  'annual_medical', 'plan_behaviour', 'plan_safety', 'plan_medical',
  'plan_incident', 'plan_community',
];

export async function loadMeetings(studentId) {
  const meetingsRef = collection(db, 'students', studentId, 'meetings');
  try {
    // A scoped role's LIST is ALWAYS rules-denied (see the catch below) — for
    // classroom teachers that meant every meetings load paid a guaranteed-
    // failing round-trip before the real per-type queries even started. On
    // school wifi that wait is what made meetings "feel flaky" / show empty
    // (30 Aug 2026). Skip straight to the fallback for restricted scopes.
    const { hasRestrictedScope } = await import('../auth.js');
    if (hasRestrictedScope()) throw Object.assign(new Error('scoped-role: skip list'), { code: 'skip-list' });
    const snap = await getDocs(query(meetingsRef, orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // LISTING /meetings is only provable by the PRODUCTION rules engine for
    // roles whose access is uniform across every meeting zone (the read rule
    // keys off each doc's `type` → meetings/plans/incidents/confidential).
    // Leadership lists fine; a scoped classroom teacher was denied wholesale —
    // so a behaviour plan Liz saved was invisible to the child's own teacher,
    // not because of the plan but because the whole list read failed. (The
    // EMULATOR evaluates list rules per-document, which is why e2e never
    // caught it.) Fall back to one provable query per meeting type: each
    // where('type','==',…) pins the zone, so the role gets exactly the types
    // it may read and the rest fail individually and are skipped.
    console.warn('[EASY] meetings LIST denied — falling back to per-type reads:', err?.code || err?.message);
    const configTypes = Object.keys(window?.nccd?.config?.meetingTypes?.types || {});
    const types = [...new Set([...MEETING_TYPE_IDS, ...configTypes])];
    const out = [];
    const transientFails = [];
    await Promise.all(types.map(async (t) => {
      try {
        const snap = await getDocs(query(meetingsRef, where('type', '==', t)));
        snap.docs.forEach(d => out.push({ id: d.id, ...d.data() }));
      } catch (e) {
        // A rules denial means this role may not read that type — absent by
        // design, stay silent. ANY OTHER failure (timeout, offline blip) means
        // meetings that DO exist would silently vanish from the list — staff
        // watched a meeting "disappear" mid-morning and panic (Sam, 1 Sep
        // 2026). Surface those as a load failure so the tab shows
        // "Couldn't load — nothing has been lost" + Retry instead.
        if (e?.code !== 'permission-denied') transientFails.push(t);
      }
    }));
    if (transientFails.length) {
      throw Object.assign(
        new Error('meetings partially failed to load (' + transientFails.join(', ') + ')'),
        { code: 'partial-load' },
      );
    }
    const ts = (m) => (m.createdAt?.toDate ? m.createdAt.toDate().getTime() : 0);
    return out.sort((a, b) => ts(b) - ts(a));
  }
}

export async function saveMeeting(studentId, meetingData) {
  const meetingsRef = collection(db, 'students', studentId, 'meetings');
  // Existing meeting: use updateDoc with the full data object so the `data` field
  // is REPLACED rather than deep-merged. Deep merge was causing previously-saved
  // categories inside data.localServices.byCategory (and similar nested maps) to
  // stick around even after a fresh search.
  const who = await whoStamp();
  if (meetingData.id) {
    const docRef = doc(db, 'students', studentId, 'meetings', meetingData.id);
    // ===== CONCURRENT-SAVE MERGE (baseline-gated) =====
    // Amanda's 09:22 whole-doc save erased Andrew's 09:21 minutes (Charlie
    // Carolan, 7 Sep 2026) because the LAST form on screen replaced the whole
    // meeting. With a baseline registered (student-detail registers one when
    // a meeting is opened), merge this save onto a fresh server read: fields
    // this editor touched win; a colleague's concurrent edits survive. The
    // FULL-payload flavour keeps deliberate deletions working (the meeting
    // form scrapes every field, so absence = removed — the localServices
    // stale-keys fix depends on that). No baseline / merge failure → the
    // exact historical replace.
    let dataToWrite = meetingData.data || {};
    try {
      const pm = await import('./profile-merge.js');
      const baseline = pm.getMeetingBaseline(meetingData.id);
      if (baseline !== undefined) {
        const snap = await getDoc(docRef);
        const server = (snap.exists() && snap.data()?.data) || {};
        dataToWrite = pm.mergeChangesFull(server, baseline || {}, meetingData.data || {});
        pm.setMeetingBaseline(meetingData.id, dataToWrite);
      }
    } catch (e) { console.warn('[EASY] saveMeeting merge skipped (plain write):', e?.message || e); dataToWrite = meetingData.data || {}; }
    const updates = {
      type: meetingData.type,
      data: dataToWrite,
      updatedAt: serverTimestamp(),
      updatedBy: who.updatedBy,
      updatedByEmail: who.updatedByEmail,
    };
    await updateDoc(docRef, updates);
    // Save history — the restore net Charlie's minutes never had.
    try {
      const json = JSON.stringify(dataToWrite);
      addDoc(collection(db, 'students', studentId, 'meetings', meetingData.id, 'history'), {
        at: serverTimestamp(), by: who.updatedBy, byEmail: who.updatedByEmail,
        bytes: json.length, data: json.length > 400000 ? json.slice(0, 400000) : json, truncated: json.length > 400000,
      }).catch(() => {});
    } catch { /* never blocks a save */ }
    return meetingData.id;
  }
  // New meeting: setDoc without merge so the document is exactly what we wrote.
  const id = doc(meetingsRef).id;
  await setDoc(doc(db, 'students', studentId, 'meetings', id), {
    ...meetingData,
    id,
    // NCCD collection period this meeting belongs to (from its date, else active).
    collectionPeriod: meetingData.collectionPeriod || activeCollectionPeriod() || inferCollectionPeriod(meetingData.data?.meetingDate),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: who.updatedBy,
    updatedBy: who.updatedBy,
    updatedByEmail: who.updatedByEmail,
  });
  return id;
}

export async function deleteMeeting(studentId, meetingId) {
  await deleteDoc(doc(db, 'students', studentId, 'meetings', meetingId));
}

// === Profile ===
/**
 * Read ONE profile section as a single document get.
 *
 * loadProfile() lists the whole /profile subcollection, and a collection LIST
 * is denied outright for roles whose zone map blocks ANY section — a specialist
 * can read and write profile/termReview per-doc, yet listing the collection
 * that contains it fails. That is exactly how the specialist bulk editor
 * "couldn't load" students on live: the read was rules-denied, not slow.
 * Editors that need one section must fetch one section.
 */
export async function loadProfileSection(studentId, section) {
  const snap = await getDoc(doc(db, 'students', studentId, 'profile', section));
  return snap.exists() ? unwrapNestedArrays(snap.data()) : null;
}

// Every profile doc id the app writes. Needed for the per-doc fallback below —
// keep in lock-step with profileZone() in firestore.rules.
export const PROFILE_SECTION_IDS = [
  'selections', 'imputed', 'summary', 'goals', 'termReview', 'specialistTracker',
  'adjustments', 'assessments', 'assessmentData', 'behaviourRating', 'fba',
  'supportTriangle', 'mtss',
];

export async function loadProfile(studentId) {
  const profileRef = collection(db, 'students', studentId, 'profile');
  try {
    // Same reasoning as loadMeetings: a scoped role's /profile LIST is ALWAYS
    // rules-denied (mixed zone access can't be proven for a collection read),
    // so attempting it just adds a guaranteed-failing round-trip before the
    // real per-section reads — on every student open, for exactly the roles
    // that reported the app feeling slow. Skip straight to the fallback.
    const { hasRestrictedScope } = await import('../auth.js');
    if (hasRestrictedScope()) throw Object.assign(new Error('scoped-role: skip list'), { code: 'skip-list' });
    const snap = await getDocs(profileRef);
    const profile = {};
    // unwrapNestedArrays reverses the { __arr: [...] } wrapping saveProfile uses
    // to store table rows etc. (Firestore forbids arrays directly inside arrays).
    snap.docs.forEach(d => { profile[d.id] = unwrapNestedArrays(d.data()); });
    return profile;
  } catch (err) {
    // LISTING /profile is only provable by the rules engine for roles whose
    // access is uniform across every zone (owner/leadership tiers). For every
    // mixed-zone role — grade teachers, ESOs, specialists, intervention — the
    // per-docId zone rule can't be proven for a collection query, so the LIST
    // is denied wholesale even though each per-doc GET they're entitled to
    // would succeed. This used to fail silently: teachers wrote goals that
    // saved perfectly, then every reload rendered the tab BLANK (and a save
    // over that silently-blank form wiped the doc — the "everything wiped"
    // incidents). Fall back to per-doc GETs: each zone the role may read
    // loads; each zone it may not stays absent, exactly as intended.
    console.warn('[EASY] profile LIST denied — falling back to per-section reads:', err?.code || err?.message);
    const profile = {};
    const transientFails = [];
    await Promise.all(PROFILE_SECTION_IDS.map(async (id) => {
      try {
        const s = await getDoc(doc(db, 'students', studentId, 'profile', id));
        if (s.exists()) profile[id] = unwrapNestedArrays(s.data());
      } catch (e) {
        // Rules-denied → absent by design (silent). Transient failure → the
        // tab would render BLANK over real data — the "goals are gone"
        // reports. Surface it so the section shows its failure + Retry state
        // (and the goals save-block engages instead of saving blanks).
        if (e?.code !== 'permission-denied') transientFails.push(id);
      }
    }));
    if (transientFails.length) {
      throw Object.assign(
        new Error('profile sections partially failed to load (' + transientFails.join(', ') + ')'),
        { code: 'partial-load' },
      );
    }
    return profile;
  }
}

// School-level assessment schedule additions — extra assessments staff add to a
// year level's schedule (beyond the built-in 2026 catalog). Shape:
//   { [grade]: { [sectionId]: [assessmentName, ...] } }
// Stored at /config/assessmentSchedule (read: all org; write: leadership).
export async function loadAssessmentSchedule() {
  try {
    const snap = await getDoc(doc(db, 'config', 'assessmentSchedule'));
    return snap.exists() ? (snap.data() || {}) : {};
  } catch { return {}; }
}
export async function addScheduledAssessment(grade, sectionId, name) {
  const clean = String(name || '').trim();
  if (!grade || !sectionId || !clean) return null;
  const ref = doc(db, 'config', 'assessmentSchedule');
  const snap = await getDoc(ref);
  const data = snap.exists() ? (snap.data() || {}) : {};
  if (!data[grade]) data[grade] = {};
  if (!Array.isArray(data[grade][sectionId])) data[grade][sectionId] = [];
  if (!data[grade][sectionId].includes(clean)) data[grade][sectionId].push(clean);
  await setDoc(ref, data);
  return data;
}

// Remove a scheduled assessment column from the school schedule. Only touches
// the schedule list — any scores students already entered stay in their profile
// (and reappear if the column is re-added), so this is non-destructive.
export async function removeScheduledAssessment(grade, sectionId, name) {
  const clean = String(name || '').trim();
  if (!grade || !sectionId || !clean) return null;
  const ref = doc(db, 'config', 'assessmentSchedule');
  const snap = await getDoc(ref);
  const data = snap.exists() ? (snap.data() || {}) : {};
  if (Array.isArray(data?.[grade]?.[sectionId])) {
    data[grade][sectionId] = data[grade][sectionId].filter(n => n !== clean);
  }
  await setDoc(ref, data);
  return data;
}

// Switch ANY assessment on/off for one year level + section — the single write
// path behind the per-year-level picker (Liz's answer 1).
//
// Two different mechanisms, deliberately behind one function so callers never
// have to know which applies:
//
//   - A BUILT-IN (in DEFAULT_ROWS) lives in code and can't be deleted, so
//     switching it off records an explicit exclusion under `_disabled`.
//   - Anything else is switched on by adding it to the school's schedule list
//     and off by removing it from that list.
//
// Doing both in ONE read-modify-write also means a toggle can't half-apply:
// the old two-call shape could add to the schedule and then fail to clear
// `_disabled`, leaving an assessment that was "on" but still hidden.
//
// Non-destructive throughout: this hides or shows a COLUMN. Scores already
// entered stay in each student's profile and reappear if it's switched back
// on. Nothing here deletes student data.
//
// Shape: { [grade]: { [sectionId]: [name] }, _disabled: { [grade]: { [sectionId]: [name] } } }
export async function setAssessmentEnabled(grade, sectionId, name, enabled) {
  const clean = String(name || '').trim();
  if (!grade || !sectionId || !clean) return null;
  const { DEFAULT_ROWS } = await import('../data/assessment-catalog.js');
  const isBuiltIn = (DEFAULT_ROWS?.[sectionId]?.[grade] || []).includes(clean);

  const ref = doc(db, 'config', 'assessmentSchedule');
  const snap = await getDoc(ref);
  const data = snap.exists() ? (snap.data() || {}) : {};

  // Additions list — only relevant for non-built-ins.
  if (!isBuiltIn) {
    if (!data[grade]) data[grade] = {};
    const added = Array.isArray(data[grade][sectionId]) ? data[grade][sectionId] : [];
    data[grade][sectionId] = enabled
      ? (added.includes(clean) ? added : [...added, clean])
      : added.filter(n => n !== clean);
  }

  // Exclusion list.
  if (!data._disabled) data._disabled = {};
  if (!data._disabled[grade]) data._disabled[grade] = {};
  const off = Array.isArray(data._disabled[grade][sectionId]) ? data._disabled[grade][sectionId] : [];
  if (enabled) {
    // Always clear on enable. A name can land in both lists (added, disabled,
    // then re-enabled) and a stale exclusion would keep it hidden while the
    // picker showed it as on.
    data._disabled[grade][sectionId] = off.filter(n => n !== clean);
  } else if (isBuiltIn) {
    // Only built-ins need an exclusion; a non-built-in is off by virtue of
    // being out of the additions list above.
    data._disabled[grade][sectionId] = off.includes(clean) ? off : [...off, clean];
  } else {
    data._disabled[grade][sectionId] = off;
  }

  await setDoc(ref, data);
  return data;
}

// Recursively strip undefined values + drop top-level arrays. Firestore
// rejects both. Used as a last-line-of-defence before any setDoc.
function sanitizeForFirestore(value) {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) {
    // Firestore forbids an array DIRECTLY inside another array (e.g. table
    // rows: [[a,b],[c,d]]). Wrap any nested array as { __arr: [...] }; it's
    // transparently unwrapped again by unwrapNestedArrays() on load.
    return value.map(v => {
      if (v === undefined) return null;
      const s = sanitizeForFirestore(v);
      return Array.isArray(s) ? { __arr: s } : s;
    });
  }
  if (typeof value === 'object' && value.constructor === Object) {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = sanitizeForFirestore(v);
    }
    return out;
  }
  return value;
}

// Inverse of the sanitize step's nested-array wrapping: turn { __arr: [...] }
// back into a plain array, recursively, so callers never see the wrapper.
function unwrapNestedArrays(value) {
  if (Array.isArray(value)) return value.map(unwrapNestedArrays);
  if (value && typeof value === 'object') {
    if (Array.isArray(value.__arr) && Object.keys(value).length === 1) return unwrapNestedArrays(value.__arr);
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = unwrapNestedArrays(v);
    return out;
  }
  return value;
}

export async function saveProfile(studentId, section, data) {
  // ===== CONCURRENT-SAVE MERGE (baseline-gated) =====
  // Whole-doc last-write-wins is the school's #1 data-loss class (Charlie's
  // minutes, Mel's specialist adjustments, the 7-Sep goals storm). When the
  // page registered a baseline for this section (student-detail does, on
  // every server hydration), merge this save onto a FRESH server read so a
  // colleague's concurrent work survives. No baseline → the exact historical
  // behaviour. A failed merge read falls back to the plain write — merging
  // must never be the reason a save doesn't land.
  let payload = data;
  try {
    const pm = await import('./profile-merge.js');
    const baseline = pm.getSectionBaseline(studentId, section);
    if (baseline !== undefined) {
      const normalise = (v) => Array.isArray(v) ? { items: v } : (v && typeof v === 'object' ? v : { value: v });
      const snap = await getDoc(doc(db, 'students', studentId, 'profile', section));
      const server = snap.exists() ? unwrapNestedArrays(snap.data()) : {};
      payload = pm.sectionMerge(section, normalise(server), normalise(baseline), normalise(data));
      pm.setSectionBaseline(studentId, section, payload); // next save diffs against what we wrote
    }
  } catch (e) { console.warn('[EASY] saveProfile merge skipped (plain write):', section, e?.message || e); payload = data; }

  // Top-level arrays are not allowed; wrap them.
  let safe = sanitizeForFirestore(payload);
  if (Array.isArray(safe)) safe = { items: safe };
  if (safe == null || typeof safe !== 'object') safe = { value: safe };
  // Always stamp who/when so every student "area" carries a last-updated
  // provenance the UI can surface on hover and the PDF can print.
  const who = await whoStamp();
  await setDoc(doc(db, 'students', studentId, 'profile', section),
    { ...safe, collectionPeriod: activeCollectionPeriod(), updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail },
    { merge: true });
  // ===== SAVE HISTORY (fire-and-forget) =====
  // A compact snapshot per save, per section — the version history that turns
  // any future clobber into a minutes-long restore instead of a backup drill
  // (same pattern the MTSS triangle has used all along).
  try {
    const json = JSON.stringify(safe);
    addDoc(collection(db, 'students', studentId, 'profile', section, 'history'), {
      at: serverTimestamp(), by: who.updatedBy, byEmail: who.updatedByEmail,
      bytes: json.length,
      data: json.length > 400000 ? json.slice(0, 400000) : json,
      truncated: json.length > 400000,
    }).catch(() => {});
  } catch { /* history must never block a save */ }
  // Keep the denormalised document counts on the student doc in sync so the
  // Records Diagnostic/General doc columns render without loading every profile.
  if (section === 'summary') {
    try { await denormaliseDocMetaOntoStudent(studentId, data); } catch { /* best-effort */ }
  }
}

/**
 * Count + latest "collected" (upload) date for each document bucket, from a
 * profile.summary object. Shape: { diag: {n, latest}, gen: {n, latest} }.
 * `latest` is an ISO string ('' when the bucket is empty).
 */
export function computeDocMeta(summary) {
  const meta = (files) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    const dates = list.map(f => f && f.uploadedAt).filter(Boolean).sort();
    return { n: list.length, latest: dates.length ? dates[dates.length - 1] : '' };
  };
  return { diag: meta(summary && summary.diagnosticFiles), gen: meta(summary && summary.files) };
}

/** Persist the doc-count summary onto the student doc (cheap Records reads). */
export async function denormaliseDocMetaOntoStudent(studentId, summary) {
  await updateDoc(doc(db, 'students', studentId), { docMeta: computeDocMeta(summary) });
}

// === Confidential ===
// Owner / Leadership-only data: a Community Support Plan (single doc) +
// a list of Confidential Notes (collection). Stored under the student
// at /students/{id}/confidential. Excluded from PDF + AI bundles.
export async function loadCommunityPlan(studentId) {
  const ref = doc(db, 'students', studentId, 'confidential', 'communityPlan');
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}
export async function saveCommunityPlan(studentId, data) {
  const safe = sanitizeForFirestore(data) || {};
  const who = await whoStamp();
  await setDoc(doc(db, 'students', studentId, 'confidential', 'communityPlan'), { ...safe, updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail }, { merge: true });
}
// Home Situation Information Sheet — sensitive background form on the
// Confidential tab. Singleton doc under each student's confidential
// subcollection (META_IDS in loadConfidentialNotes filters it out).
export async function loadHomeSituation(studentId) {
  const ref = doc(db, 'students', studentId, 'confidential', 'homeSituation');
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}
export async function saveHomeSituation(studentId, data) {
  const safe = sanitizeForFirestore(data) || {};
  const who = await whoStamp();
  await setDoc(doc(db, 'students', studentId, 'confidential', 'homeSituation'), { ...safe, updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail }, { merge: true });
}
export async function loadConfidentialNotes(studentId) {
  const ref = collection(db, 'students', studentId, 'confidential');
  const snap = await getDocs(query(ref, orderBy('createdAt', 'desc')));
  // Filter out the singleton meta docs (communityPlan, docs, homeSituation) — only note docs returned
  const META_IDS = new Set(['communityPlan', 'docs', 'homeSituation']);
  return snap.docs.filter(d => !META_IDS.has(d.id)).map(d => ({ id: d.id, ...d.data() }));
}

// Upload one or more diagnostic documents for a student: pushes each file to
// Storage under students/{id}/diagnostic/ and appends it to
// profile.summary.diagnosticFiles (same shape the Summary tab uses). Returns
// { added, total }. Shared by the Summary tab and the bulk-diagnoses editor.
export async function uploadDiagnosticDocs(studentId, files) {
  const list = Array.from(files || []);
  if (!list.length) return { added: 0, total: 0 };
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  const { storage } = await import('../firebase.js');
  const entries = [];
  for (let i = 0; i < list.length; i++) {
    const file = list[i];
    const safeName = String(file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `students/${studentId}/diagnostic/${Date.now()}_${i}_${safeName}`;
    const r = ref(storage, path);
    await uploadBytes(r, file, { contentType: file.type });
    const url = await getDownloadURL(r);
    entries.push({
      id: 'df_' + Date.now().toString(36) + '_' + i,
      name: file.name, url, type: file.type, size: file.size, path,
      uploadedAt: new Date().toISOString(),
    });
  }
  const total = await appendSummaryFiles(studentId, 'diagnosticFiles', entries);
  return { added: entries.length, total };
}

// Atomically append file entries to a summary array field (diagnosticFiles /
// files) using arrayUnion, so two overlapping uploads to the same student can't
// clobber each other (a plain load→push→setDoc-merge replaces the whole array).
// Re-reads afterwards to keep the denormalised docMeta counts in sync.
export async function appendSummaryFiles(studentId, field, entries) {
  const who = await whoStamp();
  const safeEntries = entries.map(e => sanitizeForFirestore(e));
  await setDoc(doc(db, 'students', studentId, 'profile', 'summary'), {
    [field]: arrayUnion(...safeEntries),
    collectionPeriod: activeCollectionPeriod(),
    updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail,
  }, { merge: true });
  const fresh = (await loadProfile(studentId))?.summary || {};
  try { await denormaliseDocMetaOntoStudent(studentId, fresh); } catch { /* best-effort */ }
  return Array.isArray(fresh[field]) ? fresh[field].length : entries.length;
}

// Remove ONE file from a summary array field, atomically and by identity — a
// transaction reads the FRESH array and filters out the entry by id (or path),
// so it (a) can't clobber a concurrent add/remove and (b) always removes the
// right file even if the array shifted since render (a stale-index splice+save
// could delete the wrong document). Re-syncs docMeta afterwards.
export async function removeSummaryFile(studentId, field, { id, path } = {}) {
  const ref = doc(db, 'students', studentId, 'profile', 'summary');
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = (snap.exists() && Array.isArray(snap.data()[field])) ? snap.data()[field] : [];
    const next = cur.filter(f => !(f && ((id && f.id === id) || (path && f.path === path))));
    tx.set(ref, { [field]: next, updatedAt: serverTimestamp() }, { merge: true });
  });
  const fresh = (await loadProfile(studentId))?.summary || {};
  try { await denormaliseDocMetaOntoStudent(studentId, fresh); } catch { /* best-effort */ }
}

// Upload one or more GENERAL documents (year-stamped evidence) for a student:
// pushes each file to Storage under students/{id}/general/ and appends it to
// profile.summary.files with a `year` stamp. Mirrors uploadDiagnosticDocs so
// the document drive can add to either bucket. Returns { added, total }.
export async function uploadGeneralDocs(studentId, files, year) {
  const list = Array.from(files || []);
  if (!list.length) return { added: 0, total: 0 };
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  const { storage } = await import('../firebase.js');
  const yr = String(year || new Date().getFullYear());
  const entries = [];
  for (let i = 0; i < list.length; i++) {
    const file = list[i];
    const safeName = String(file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `students/${studentId}/general/${Date.now()}_${i}_${safeName}`;
    const r = ref(storage, path);
    await uploadBytes(r, file, { contentType: file.type });
    const url = await getDownloadURL(r);
    entries.push({
      id: 'gf_' + Date.now().toString(36) + '_' + i,
      name: file.name, url, type: file.type, size: file.size, path, year: yr,
      uploadedAt: new Date().toISOString(),
    });
  }
  const total = await appendSummaryFiles(studentId, 'files', entries);
  return { added: entries.length, total };
}

/** How many diagnostic docs a student has (best-effort; loads the profile). */
export async function getDiagnosticDocCount(studentId) {
  try {
    const profile = await loadProfile(studentId);
    return Array.isArray(profile?.summary?.diagnosticFiles) ? profile.summary.diagnosticFiles.length : 0;
  } catch { return 0; }
}

/** The diagnostic docs ({name,url,...}) a student already has. */
export async function getDiagnosticDocs(studentId) {
  try {
    const profile = await loadProfile(studentId);
    return Array.isArray(profile?.summary?.diagnosticFiles) ? profile.summary.diagnosticFiles : [];
  } catch { return []; }
}

// Confidential documents (file attachments) — singleton doc holding an
// array of { name, url, type, size, path, uploadedAt, uploadedBy } objects.
// Same shape as profile.summary.files / .diagnosticFiles so the existing
// renderFileTile() helper Just Works.
export async function loadConfidentialDocs(studentId) {
  const ref = doc(db, 'students', studentId, 'confidential', 'docs');
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() || {}) : { files: [] };
}
export async function saveConfidentialDocs(studentId, payload) {
  const safe = sanitizeForFirestore(payload) || {};
  const who = await whoStamp();
  await setDoc(doc(db, 'students', studentId, 'confidential', 'docs'), { ...safe, updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail }, { merge: true });
}
export async function saveConfidentialNote(studentId, note) {
  const safe = sanitizeForFirestore(note) || {};
  const who = await whoStamp();
  if (note.id) {
    await setDoc(doc(db, 'students', studentId, 'confidential', note.id), { ...safe, updatedAt: serverTimestamp(), updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail }, { merge: true });
    return note.id;
  }
  const ref = await addDoc(collection(db, 'students', studentId, 'confidential'), {
    ...safe, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: who.updatedBy, updatedBy: who.updatedBy, updatedByEmail: who.updatedByEmail,
  });
  return ref.id;
}
export async function deleteConfidentialNote(studentId, noteId) {
  if (!noteId || noteId === 'communityPlan') return;
  await deleteDoc(doc(db, 'students', studentId, 'confidential', noteId));
}

// === Emails ===
export async function loadEmails(studentId) {
  const emailsRef = collection(db, 'students', studentId, 'emails');
  const snap = await getDocs(query(emailsRef, orderBy('sentAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveEmail(studentId, emailData) {
  const emailsRef = collection(db, 'students', studentId, 'emails');
  await addDoc(emailsRef, { ...emailData, sentAt: serverTimestamp() });
}

// Edit/delete only apply to record-only entries — emails that were
// actually sent through Resend are an immutable audit trail. The UI
// hides the buttons on non-record-only rows; the server-side guard
// here is a belt-and-braces check.
export async function updateEmail(studentId, emailId, patch) {
  if (patch.category && patch.category !== 'record_only') {
    throw new Error('Only record-only emails can be edited');
  }
  await updateDoc(doc(db, 'students', studentId, 'emails', emailId), patch);
}

export async function deleteEmail(studentId, emailId) {
  await deleteDoc(doc(db, 'students', studentId, 'emails', emailId));
}

// === Goal evidence (Storage uploads attached to /profile/goals) ===
//
// Files are stored at
//   students/{studentId}/goalEvidence/{termKey}/goal_{idx}/{ts}_{safeName}
// and the descriptor (name, size, type, url, storagePath) is appended
// to the goal's evidenceFiles array on save. Storage rules already
// allow auth users full access under students/{studentId}/** — see
// app/storage.rules.

export async function uploadGoalEvidenceFile(studentId, termKey, goalIdx, file) {
  if (!file) throw new Error('No file selected');
  if (file.size > 25 * 1024 * 1024) throw new Error('File too large - 25 MB max');
  const safeName = (file.name || 'evidence').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `students/${studentId}/goalEvidence/${encodeURIComponent(termKey || 'unsorted')}/goal_${goalIdx}/${Date.now()}_${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(ref);
  return {
    name: file.name,
    size: file.size,
    contentType: file.type || '',
    url,
    storagePath: path,
    uploadedAt: new Date().toISOString(),
  };
}

// === Email attachments ====================================================
//
// Attachments used to exist only as base64 in the Resend payload, and the
// history doc kept nothing but a list of FILENAMES - so nobody could open what
// was actually sent, and "Save without sending" dropped the files entirely.
// Every attachment is now uploaded to Storage first and the email doc carries a
// real descriptor, so the history card can link to the file either way.
export async function uploadEmailAttachment(studentId, file) {
  if (!file) throw new Error('No file selected');
  const safeName = (file.name || 'attachment').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `students/${studentId}/emailAttachments/${Date.now()}_${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(ref);
  return {
    name: file.name,
    size: file.size,
    contentType: file.type || '',
    url,
    storagePath: path,
  };
}

export async function deleteGoalEvidenceFile(storagePath) {
  if (!storagePath) return;
  try {
    await deleteObject(storageRef(storage, storagePath));
  } catch (e) {
    // Swallow — the Firestore-side removal still needs to succeed
    // even if the Storage object is already gone (e.g. previously
    // half-deleted, or saved before Storage wiring was added).
    console.warn('Could not delete goal-evidence file', storagePath, e?.message);
  }
}

/**
 * Send an email via the Cloud Function (Resend transactional sender).
 *
 * The function builds the From header as `<Teacher Name> <nccd@<school
 * domain>>` and sets Reply-To to the teacher's own email so replies route
 * back to them. The teacher's display name still appears in the
 * recipient's inbox even though the envelope sender is the school
 * domain. School-specific from address / domain is configured per
 * project in Firestore at /settings/email (see Cloud Function comments).
 *
 * Also records the email under students/{id}/emails for history (done
 * server-side so the client doesn't need to round-trip).
 */
export async function sendEmail(studentId, { to, subject, body, cc, bcc, category, attachments, attachmentFiles }) {
  if (!to || !subject) throw new Error('Recipient and subject required');
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../firebase.js');
  const sendEmailFn = httpsCallable(functions, 'sendEmail');
  const res = await sendEmailFn({
    to, cc, bcc, subject, body, studentId, category: category || 'manual',
    attachments: attachments?.length ? attachments : undefined,
  });
  const providerId = res.data?.id || null;
  // The function writes the history doc itself with filenames only. Patch the
  // openable descriptors on afterwards so the history card can link to what was
  // actually sent. A failure here must not read as a failed send.
  if (studentId && attachmentFiles?.length) {
    try {
      const snap = await getDocs(query(
        collection(db, 'students', studentId, 'emails'),
        orderBy('sentAt', 'desc'),
        limit(1),
      ));
      const latest = snap.docs[0];
      if (latest) await updateDoc(latest.ref, { attachmentFiles });
    } catch (e) {
      console.warn('Email sent but attachment links could not be recorded:', e?.message);
    }
  }
  return providerId;
}

/**
 * Send a one-off invite email to a newly-added user. Goes through the
 * same Cloud Function `sendEmail` as student emails — the recipient sees
 * the inviting admin's name in their inbox; replies route to the admin.
 */
export async function sendInviteEmail({ to, displayName, schoolName, role, signInUrl, practiceUrl }) {
  if (!to) throw new Error('Recipient required');
  const { getCurrentUser } = await import('../auth.js');
  const currentUser = getCurrentUser();
  if (!currentUser?.email) throw new Error('You must be signed in to send an invite');
  const { subject, html } = buildInviteEmail({ to, displayName, role, signInUrl });

  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../firebase.js');
  const sendEmailFn = httpsCallable(functions, 'sendEmail');
  const res = await sendEmailFn({
    to, subject, html, category: 'invite',
    fromName: 'Elizabeth Pentogenis',
    replyTo: 'help@easyclarity4growth.com',
  });
  return res.data?.id || null;
}

/** Build the invite email EXACTLY as it will send — shared by sendInviteEmail
 *  and the 👁 Preview in Settings so what you see is what goes out. */
export function buildInviteEmail({ to = '', displayName = '', role = '', signInUrl = '' } = {}) {
  // ONE link only: the site this invite was sent FROM. (Earlier invites listed
  // both the practice and real schools, which confused new users about which
  // to open — send practice invites from practice, real invites from real.)
  const origin = signInUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const hostOf = (u) => { try { return new URL(u).hostname; } catch { return ''; } };
  const isPractice = (u) => /practice4/i.test(hostOf(u));
  const fromPractice = isPractice(origin);

  const subject = 'Welcome to Easy Clarity 4 Growth – Less NCCD Paperwork, More Time for Students';
  const GREEN = '#4a7d3f';
  // Image host must match the SENDING domain (or a subdomain of it) — Gmail
  // treats mismatched image hosts as suspicious. Every school site serves
  // /easy-logo.png, so when this environment runs on its own
  // *.easyclarity4growth.com domain use THAT (verified: stbrigids + practice4
  // subdomains + root all 200); on *.web.app origins fall back to the root
  // sending domain.
  const _host = (typeof window !== 'undefined' && window.location.hostname) || '';
  const LOGO = (/(^|\.)easyclarity4growth\.com$/.test(_host))
    ? `https://${_host}/easy-logo.png`
    : 'https://easyclarity4growth.com/easy-logo.png';
  const btn = (href, label) => `<a href="${href}" style="display:inline-block;padding:11px 20px;background:${GREEN};color:#fff;text-decoration:none;border-radius:6px;font-weight:bold">${label}</a>`;

  const linksBlock = `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:6px 0 4px"><tr>
         <td style="padding:0 0 10px 0">${btn(origin, fromPractice ? '🧪 Open the Practice School' : '🏫 Open your school')}</td>
       </tr></table>
       ${fromPractice
    ? '<p style="margin:6px 0 0">This is the <strong>Practice School</strong> - a pretend school just for exploring. Click anything, try everything - you <strong>cannot damage any real data</strong> here.</p>'
    : '<p style="margin:6px 0 0">Sign in with your usual school Google or Microsoft account - your access is already set up.</p>'}`;

  const html = `
    <div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.55;color:#2d2a26;max-width:620px">
      <div style="text-align:center;margin-bottom:8px"><img src="${LOGO}" alt="Easy Clarity 4 Growth" width="150" style="max-width:150px;height:auto"></div>
      <h1 style="color:${GREEN};font-size:22px;line-height:1.3;margin:6px 0 16px">Welcome to Easy Clarity 4 Growth - less paperwork, more time for your students</h1>
      <p>Hi ${displayName ? escInvite(displayName.split(/\s+/)[0]) : 'there'},</p>
      <p>We're delighted to introduce <strong>Easy Clarity 4 Growth</strong>, our NCCD program built to support you and take the heavy lifting out of NCCD documentation. It's designed to reduce the time you spend on paperwork so you can spend more of it where it matters most - with your students.</p>
      ${role ? `<p style="margin:0 0 14px">Your role: <strong>${escInvite(String(role).replace(/_/g, ' '))}</strong> - sign in with <strong>${escInvite(to)}</strong>.</p>` : ''}
      <h2 style="color:${GREEN};font-size:17px;margin:22px 0 8px">${fromPractice ? 'Try before you fly - the Practice School' : 'Your sign-in link'}</h2>
      ${linksBlock}
      <h2 style="color:${GREEN};font-size:17px;margin:22px 0 8px">Help is built right in</h2>
      <p>You won't be left guessing - the program includes <strong>videos</strong> and <strong>step-by-step guides</strong> for every task, so you can learn at your own pace whenever it suits you.</p>
      <p>For any technical questions or a helping hand, contact <a href="mailto:help@easyclarity4growth.com" style="color:${GREEN}">help@easyclarity4growth.com</a>.</p>
      <p>If you have any other questions, just reply to this email - we're here to help.</p>
      <p style="margin-top:22px">Warm regards,</p>
      <p style="margin:0"><strong>Elizabeth Pentogenis</strong><br>Director<br>Easy Clarity 4 Growth Pty Ltd<br><a href="mailto:elizabeth@easyclarity4growth.com" style="color:${GREEN}">elizabeth@easyclarity4growth.com</a></p>
    </div>`;

  return { subject, html };
}

// Minimal HTML-escape for the few interpolated values in the invite email.
function escInvite(s) {
  return String(s == null ? '' : s).replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
}

// === Access Log ===
export async function loadAccessLog(studentId) {
  const logRef = collection(db, 'students', studentId, 'accessLog');
  const snap = await getDocs(query(logRef, orderBy('timestamp', 'desc')));
  return snap.docs.map(d => d.data()).slice(0, 100);
}

export async function logAccess(studentId, action) {
  // BEST-EFFORT, like logAudit below: an audit line must never be able to
  // fail the action it records. When this threw, the throw landed AFTER the
  // real write had committed — the user saw "You don't have permission" over
  // a note that had in fact saved, and retrying duplicated it.
  try {
    const logRef = collection(db, 'students', studentId, 'accessLog');
    await addDoc(logRef, {
      timestamp: serverTimestamp(),
      userEmail: (await import('../auth.js')).getCurrentUser()?.email || 'unknown',
      action,
    });
  } catch (err) {
    console.warn('[EASY] access-log write failed (action already completed):', err?.code || err?.message);
  }
}

// Central, immutable audit trail (/auditLog) for high-consequence actions —
// notably document deletions. Best-effort: failures are logged, not thrown, so
// a deletion still completes even if the audit write is blocked. The rules
// require userUid to match the caller, so we always stamp it.
export async function logAudit(action, details = {}) {
  try {
    const { getCurrentUser } = await import('../auth.js');
    const u = getCurrentUser();
    await addDoc(collection(db, 'auditLog'), {
      timestamp: serverTimestamp(),
      userUid: u?.uid || '',
      userEmail: u?.email || 'unknown',
      action,
      ...details,
    });
  } catch (e) {
    console.warn('audit log write failed:', e?.message || e);
  }
}
